import { CDN_DOMAIN, fromCDN } from '@/cdn';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import { YM_ID } from '@/metrics/ym';

interface Props {
    meta: Partial<{
        baseTitle: string;
        title: string;
        description: string;
        ogImage: string;
    }>;
}

const AppHead = ({ meta }: Props) => {
    const pathname = usePathname();
    const title = meta.title ? `${meta.title} — ${meta.baseTitle}` : meta.baseTitle;

    return (
        <Head>
            <meta charSet="utf-8" />
            <title>{title}</title>
            <meta name="description" content={meta.description} />
            <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
            {process.env.NEXT_PUBLIC_HOST && (
                <meta property="og:url" content={process.env.NEXT_PUBLIC_HOST + (pathname === '/' ? '' : pathname)} />
            )}
            <meta property="og:locale" content="ru" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={meta.description} />
            <meta property="og:image" content={meta.ogImage} />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:image" content={meta.ogImage} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={meta.description} />
            <meta name="yandex-verification" content="7902d6757b79ab6d" />

            <link rel="icon" href={fromCDN('/img/favicon/favicon.ico')} />
            <link rel="icon" type="image/png" sizes="32x32" href={fromCDN('/img/favicon/favicon-32x32.png')} />
            <link rel="icon" type="image/png" sizes="16x16" href={fromCDN('/img/favicon/favicon-16x16.png')} />
            <link rel="icon" href={fromCDN('/img/favicon/favicon.svg')} type="image/svg+xml" />
            <link rel="apple-touch-icon" sizes="180x180" href={fromCDN('/img/favicon/apple-touch-icon.png')} />

            <link
                rel="canonical"
                href={`${(process.env.NEXT_PUBLIC_HOST || '').replace(/\/$/, '')}${pathname}`.replace(/\/$/, '')}
            />

            {process.env.NODE_ENV === 'production' && (
                <>{process.env.NEXT_PUBLIC_ENV === 'production' && <link rel="preconnect" href={CDN_DOMAIN} />}</>
            )}
            <link
                rel="preload"
                href={fromCDN('/img/logo-rotormine.svg')}
                as="image"
                type="image/svg+xml"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                href={fromCDN('/fonts/PPNeueMontrealMono-Medium.woff2')}
                as="font"
                type="font/woff2"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                href={fromCDN('/fonts/TT_Neoris_Regular.woff2')}
                as="font"
                type="font/woff2"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                href={fromCDN('/fonts/TT_Neoris_Medium.woff2')}
                as="font"
                type="font/woff2"
                crossOrigin="anonymous"
            />
        </Head>
    );
};

export default AppHead;
